// =====================================================
// ADMIN SECURITY PANEL  (Point 10.12 + Point 11 admin visibility)
//
// A self-contained additive panel embedded inside AdminSalesExecutives (so it
// shows on /sales-admin AND inside the main Admin Dashboard, exactly where the
// existing derived Fraud Reports already render). It surfaces the STORED
// security system:
//   • Fraud alerts (live, newest first) + counters
//   • Fraud Reports — filterable/sortable flagged-user list with drill-down
//   • Confirm Fraud / Mark False Positive actions
//   • Repeat-offender executives (uses the existing suspend/remove controls
//     in the executives table above — this only flags who to act on)
//   • Blocked Devices — refused registrations + the whitelist override (CR-01)
//   • Disposable-domain blocklist management (10.9)
//   • T&C acceptances (who accepted which version, when) + publish new version
//
// Styling reuses the existing `asx-` classes plus a few `asec-` additions
// (AdminSecurityPanel.css). Nothing outside this file/needs to change beyond
// the single mount line in AdminSalesExecutives.
// =====================================================

import { useCallback, useEffect, useState } from "react";
import { salesAdmin, errorMessage } from "../services/salesApi";
import "./AdminSecurityPanel.css";

const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");

const bandClass = (band) =>
  band === "FRAUD" ? "asec-band-fraud" : band === "SUSPICIOUS" ? "asec-band-suspicious" : "asec-band-clean";

export default function AdminSecurityPanel() {
  const [alerts, setAlerts] = useState([]);
  const [reports, setReports] = useState([]);
  const [offenders, setOffenders] = useState([]);
  const [domains, setDomains] = useState({ bundledCount: 0, custom: [] });
  const [acceptances, setAcceptances] = useState([]);
  const [versions, setVersions] = useState([]);
  const [activeVersion, setActiveVersion] = useState(0);
  // CR-01 — blocked device attempts + the whitelist override.
  const [blocked, setBlocked] = useState({ items: [], blockEnabled: true });
  const [whitelist, setWhitelist] = useState([]);
  const [deviceSearch, setDeviceSearch] = useState(""); // applied search term
  const [deviceSearchInput, setDeviceSearchInput] = useState("");

  const [bandFilter, setBandFilter] = useState(""); // "", SUSPICIOUS, FRAUD
  const [detail, setDetail] = useState(null); // drill-down
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [newDomain, setNewDomain] = useState("");

  const load = useCallback(async () => {
    setErr("");
    try {
      const [a, r, o, d, acc, ver, bd, wl] = await Promise.all([
        salesAdmin.fraudAlerts({ limit: 50 }),
        salesAdmin.fraudUserReports({ band: bandFilter || undefined, limit: 100 }),
        salesAdmin.repeatOffenders(),
        salesAdmin.listDisposableDomains(),
        salesAdmin.termsAcceptances({ limit: 50 }),
        salesAdmin.termsVersions(),
        salesAdmin.listBlockedDevices({ q: deviceSearch || undefined, limit: 50 }),
        salesAdmin.listDeviceWhitelist(),
      ]);
      setAlerts(a.alerts || []);
      setReports(r.items || []);
      setOffenders(o.offenders || []);
      setDomains({ bundledCount: d.bundledCount || 0, custom: d.custom || [] });
      setAcceptances(acc.items || []);
      setVersions(ver.versions || []);
      setActiveVersion(ver.activeVersion || 0);
      setBlocked({ items: bd.items || [], blockEnabled: bd.blockEnabled !== false });
      setWhitelist(wl.items || []);
    } catch (e) {
      setErr(errorMessage(e, "Could not load the security panel"));
    }
  }, [bandFilter, deviceSearch]);

  useEffect(() => {
    load();
  }, [load]);

  const flash = (m) => {
    setMsg(m);
    setTimeout(() => setMsg(""), 3000);
  };

  const openDetail = async (userId) => {
    setDetail({ loading: true });
    try {
      const d = await salesAdmin.fraudUserDetail(userId);
      setDetail(d);
    } catch (e) {
      setErr(errorMessage(e, "Could not load user detail"));
      setDetail(null);
    }
  };

  const confirmFraud = async (userId) => {
    if (!window.confirm("Confirm fraud PERMANENTLY? The user will not count as a valid user and derived incentives are reversed.")) return;
    setBusy(userId);
    try {
      await salesAdmin.confirmFraud(userId);
      flash("Fraud confirmed.");
      await load();
      if (detail && detail.user && String(detail.user._id) === String(userId)) openDetail(userId);
    } catch (e) {
      setErr(errorMessage(e, "Action failed"));
    } finally {
      setBusy("");
    }
  };

  const falsePositive = async (userId) => {
    if (!window.confirm("Mark as FALSE POSITIVE? Valid-user status and any reversed incentive will be restored.")) return;
    setBusy(userId);
    try {
      await salesAdmin.markFalsePositive(userId);
      flash("Marked as false positive; valid status restored.");
      await load();
      if (detail && detail.user && String(detail.user._id) === String(userId)) openDetail(userId);
    } catch (e) {
      setErr(errorMessage(e, "Action failed"));
    } finally {
      setBusy("");
    }
  };

  const addDomain = async (e) => {
    e.preventDefault();
    if (!newDomain.trim()) return;
    setBusy("domain");
    try {
      await salesAdmin.addDisposableDomain(newDomain.trim());
      setNewDomain("");
      flash("Domain added to the blocklist.");
      await load();
    } catch (e2) {
      setErr(errorMessage(e2, "Could not add domain"));
    } finally {
      setBusy("");
    }
  };

  // ── CR-01: whitelist / un-whitelist a device hash ──
  const whitelistDevice = async (deviceHash) => {
    const reason = window.prompt(
      "Whitelist this device so it can register again?\n\nOptional reason (recorded with your admin ID):",
      ""
    );
    if (reason === null) return; // cancelled
    setBusy(deviceHash);
    try {
      await salesAdmin.whitelistDevice(deviceHash, reason);
      flash("Device whitelisted; it can register again.");
      await load();
    } catch (e) {
      setErr(errorMessage(e, "Could not whitelist the device"));
    } finally {
      setBusy("");
    }
  };

  const unwhitelistDevice = async (deviceHash) => {
    const reason = window.prompt(
      "Remove this device from the whitelist? The registration block will apply to it again.\n\nOptional reason (recorded with your admin ID):",
      ""
    );
    if (reason === null) return;
    setBusy(deviceHash);
    try {
      await salesAdmin.unwhitelistDevice(deviceHash, reason);
      flash("Device removed from the whitelist; the block applies again.");
      await load();
    } catch (e) {
      setErr(errorMessage(e, "Could not remove the device"));
    } finally {
      setBusy("");
    }
  };

  const publishTerms = async () => {
    if (!window.confirm("Publish a NEW T&C version? Every Sales Executive will have to re-accept on next login.")) return;
    setBusy("terms");
    try {
      const r = await salesAdmin.publishTerms({});
      flash(`Published T&C version ${r.version}. Re-acceptance now required.`);
      await load();
    } catch (e) {
      setErr(errorMessage(e, "Could not publish new version"));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="asec-root">
      {msg ? <div className="asec-flash asec-flash-ok">{msg}</div> : null}
      {err ? <div className="asec-flash asec-flash-err">{err}</div> : null}

      {/* ══ FRAUD ALERTS ══ */}
      <div className="asx-block">
        <h3 className="asx-block-title">🛡️ Security — Fraud Alerts ({alerts.length})</h3>
        <p className="asx-hint">
          Live alerts from the security engine (device, IP, email/mobile, spam,
          bot and burst detection). Newest first.
        </p>
        {alerts.length === 0 ? (
          <div className="asx-empty asx-empty-box">No active fraud alerts.</div>
        ) : (
          <div className="asec-alert-list">
            {alerts.map((a) => (
              <div key={a.userId} className="asec-alert">
                <div className="asec-alert-top">
                  <span className="asec-band asec-band-fraud">FRAUD · {a.score}</span>
                  {a.status === "confirmed_fraud" ? (
                    <span className="asec-tag asec-tag-confirmed">confirmed</span>
                  ) : (
                    <span className="asec-tag">auto-flagged</span>
                  )}
                </div>
                <div className="asx-strong">{a.name}</div>
                <div className="asx-muted">{a.email}</div>
                {a.executive ? (
                  <div className="asec-alert-exec">via SE: {a.executive.name}</div>
                ) : null}
                {a.topSignals && a.topSignals.length ? (
                  <div className="asec-signals">{a.topSignals.join(" · ")}</div>
                ) : null}
                <div className="asec-alert-actions">
                  <button className="asx-btn asx-btn-sm" onClick={() => openDetail(a.userId)}>
                    Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ══ REPEAT OFFENDERS ══ */}
      {offenders.length > 0 && (
        <div className="asx-block">
          <h3 className="asx-block-title">⛔ Repeat-Offender Executives ({offenders.length})</h3>
          <p className="asx-hint">
            Executives with {offenders[0]?.threshold}+ fraud users among their
            referrals. Use the Suspend / Remove controls in the Executives table
            above to act.
          </p>
          <div className="asec-offenders">
            {offenders.map((o) => (
              <div key={o.executiveId} className="asec-offender">
                <span className="asx-strong">{o.name}</span>
                <span className="asx-muted">{o.email}</span>
                <span className="asec-tag asec-tag-confirmed">{o.fraudCount} fraud users</span>
                <span className={`asx-badge asx-badge-${o.status === "suspended" ? "rejected" : "active"}`}>
                  {o.blocked ? "blocked" : o.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══ FRAUD REPORTS (filterable) ══ */}
      <div className="asx-block">
        <h3 className="asx-block-title">📋 Fraud Reports — flagged users</h3>
        <div className="asec-filters">
          {["", "SUSPICIOUS", "FRAUD"].map((b) => (
            <button
              key={b || "all"}
              className={`asx-chip ${bandFilter === b ? "asx-chip-active" : ""}`}
              onClick={() => setBandFilter(b)}
            >
              {b || "All"}
            </button>
          ))}
        </div>
        {reports.length === 0 ? (
          <div className="asx-empty asx-empty-box">No flagged users for this filter.</div>
        ) : (
          <div className="asx-table-wrap">
            <table className="asx-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Executive</th>
                  <th>Score</th>
                  <th>Band</th>
                  <th>Status</th>
                  <th>Top signals</th>
                  <th>Valid?</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => (
                  <tr key={r.userId}>
                    <td>
                      <div className="asx-strong">{r.name}</div>
                      <div className="asx-muted">{r.email}</div>
                    </td>
                    <td>{r.executive ? r.executive.name : "—"}</td>
                    <td className="asec-score">{r.score}</td>
                    <td>
                      <span className={`asec-band ${bandClass(r.band)}`}>{r.band}</span>
                    </td>
                    <td>{r.status}</td>
                    <td className="asec-signals-cell">
                      {(r.signals || []).slice(0, 2).map((s) => s.label).join(", ") || "—"}
                    </td>
                    <td>{r.isValid ? "✅" : "—"}</td>
                    <td>
                      <div className="asec-row-actions">
                        <button className="asx-btn asx-btn-sm" onClick={() => openDetail(r.userId)}>
                          Details
                        </button>
                        {r.status !== "confirmed_fraud" && (
                          <button
                            className="asx-btn asx-btn-sm asx-btn-danger"
                            disabled={busy === r.userId}
                            onClick={() => confirmFraud(r.userId)}
                          >
                            Confirm
                          </button>
                        )}
                        {r.status !== "false_positive" && (
                          <button
                            className="asx-btn asx-btn-sm asx-btn-ok"
                            disabled={busy === r.userId}
                            onClick={() => falsePositive(r.userId)}
                          >
                            False +
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ══ BLOCKED DEVICES + WHITELIST (Change Request 01) ══ */}
      <div className="asx-block">
        <h3 className="asx-block-title">📱 Blocked Devices</h3>
        <p className="asx-hint">
          A device that already has an account cannot create a second one — the
          registration is refused and recorded below.{" "}
          {blocked.blockEnabled ? (
            <strong>The block is currently ON.</strong>
          ) : (
            <strong>
              The block is currently OFF (DEVICE_BLOCK_ENABLED=false) — nobody is being
              refused.
            </strong>
          )}{" "}
          Two identical phones can produce the same fingerprint, so if a genuine
          person is caught by it, whitelist their device here.
        </p>

        <form
          className="asec-domain-form"
          onSubmit={(e) => {
            e.preventDefault();
            setDeviceSearch(deviceSearchInput.trim());
          }}
        >
          <input
            className="asx-input"
            placeholder="Search by device hash or email"
            value={deviceSearchInput}
            onChange={(e) => setDeviceSearchInput(e.target.value)}
          />
          <button className="asx-btn asx-btn-sm">Search</button>
          {deviceSearch ? (
            <button
              type="button"
              className="asx-btn asx-btn-sm"
              onClick={() => {
                setDeviceSearchInput("");
                setDeviceSearch("");
              }}
            >
              Clear
            </button>
          ) : null}
        </form>

        {blocked.items.length === 0 ? (
          <div className="asx-empty asx-empty-box">
            {deviceSearch ? "No blocked attempts match that search." : "No blocked device attempts yet."}
          </div>
        ) : (
          <div className="asx-table-wrap">
            <table className="asx-table">
              <thead>
                <tr>
                  <th>Device hash</th>
                  <th>Attempted email</th>
                  <th>Referral code</th>
                  <th>Blocked at</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {blocked.items.map((b) => (
                  <tr key={b.id}>
                    <td className="asec-muted-mono" title={b.deviceHash || "no fingerprint was supplied"}>
                      {(b.deviceHash || "").slice(0, 16) || "—"}
                    </td>
                    <td>{b.attemptedEmail || "—"}</td>
                    <td>{b.referralCode || "—"}</td>
                    <td title={`IP: ${b.ip || "—"}\nUser agent: ${b.userAgent || "—"}\n${b.detail || ""}`}>
                      {fmt(b.at)}
                    </td>
                    <td>
                      {!b.deviceHash ? (
                        <span className="asec-tag">no fingerprint</span>
                      ) : b.whitelisted ? (
                        <span className="asec-tag">whitelisted</span>
                      ) : (
                        <button
                          className="asx-btn asx-btn-sm asx-btn-ok"
                          disabled={busy === b.deviceHash}
                          onClick={() => whitelistDevice(b.deviceHash)}
                        >
                          Whitelist
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <h4 className="asec-h4">Whitelisted devices ({whitelist.length})</h4>
        {whitelist.length === 0 ? (
          <div className="asx-empty asx-empty-box">No devices are whitelisted.</div>
        ) : (
          <div className="asx-table-wrap">
            <table className="asx-table">
              <thead>
                <tr>
                  <th>Device hash</th>
                  <th>Reason</th>
                  <th>Whitelisted by</th>
                  <th>When</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {whitelist.map((w) => (
                  <tr key={w._id}>
                    <td className="asec-muted-mono" title={w.deviceHash}>
                      {(w.deviceHash || "").slice(0, 16)}
                    </td>
                    <td>{w.reason || "—"}</td>
                    <td>{w.addedBy?.name || "—"}</td>
                    <td>{fmt(w.addedAt)}</td>
                    <td>
                      <button
                        className="asx-btn asx-btn-sm asx-btn-danger"
                        disabled={busy === w.deviceHash}
                        onClick={() => unwhitelistDevice(w.deviceHash)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ══ DISPOSABLE DOMAIN MANAGER ══ */}
      <div className="asx-block">
        <h3 className="asx-block-title">📧 Disposable Email Blocklist</h3>
        <p className="asx-hint">
          {domains.bundledCount} domains are bundled. Add more below — new signups
          using these domains are blocked immediately.
        </p>
        <form className="asec-domain-form" onSubmit={addDomain}>
          <input
            className="asx-input"
            placeholder="tempmail.example"
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
          />
          <button className="asx-btn asx-btn-primary asx-btn-sm" disabled={busy === "domain"}>
            Add domain
          </button>
        </form>
        {domains.custom.length > 0 && (
          <div className="asec-domain-list">
            {domains.custom.map((d) => (
              <span key={d._id} className="asec-tag">
                {d.domain}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ══ T&C ADMIN VISIBILITY ══ */}
      <div className="asx-block">
        <h3 className="asx-block-title">📜 Terms &amp; Conditions</h3>
        <div className="asec-terms-head">
          <span className="asx-hint">
            Active version: <strong>v{activeVersion}</strong> · {versions.length} version(s) published
          </span>
          <button className="asx-btn asx-btn-warn asx-btn-sm" disabled={busy === "terms"} onClick={publishTerms}>
            Publish new version (force re-accept)
          </button>
        </div>
        <div className="asx-table-wrap">
          <table className="asx-table">
            <thead>
              <tr>
                <th>Sales Executive</th>
                <th>Version</th>
                <th>Accepted at</th>
                <th>IP</th>
                <th>Device</th>
              </tr>
            </thead>
            <tbody>
              {acceptances.length === 0 ? (
                <tr>
                  <td colSpan={5} className="asx-empty">
                    No acceptances recorded yet.
                  </td>
                </tr>
              ) : (
                acceptances.map((a) => (
                  <tr key={a._id}>
                    <td>
                      <div className="asx-strong">{a.salesExecutive?.name || "—"}</div>
                      <div className="asx-muted">{a.salesExecutive?.email || ""}</div>
                    </td>
                    <td>v{a.termsVersion}</td>
                    <td>{fmt(a.acceptedAt)}</td>
                    <td className="asec-muted-mono">{a.ipAddress || "—"}</td>
                    <td className="asec-muted-mono">{(a.deviceHash || "").slice(0, 12) || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ══ DRILL-DOWN MODAL ══ */}
      {detail && (
        <div className="asx-modal-backdrop" onClick={() => setDetail(null)}>
          <div className="asx-modal" onClick={(e) => e.stopPropagation()}>
            <div className="asx-modal-head">
              <h3>🔍 Fraud detail</h3>
              <button className="asx-btn asx-btn-sm" onClick={() => setDetail(null)}>
                Close
              </button>
            </div>
            {detail.loading ? (
              <div className="asx-empty">Loading…</div>
            ) : (
              <div className="asec-detail">
                <div className="asec-detail-head">
                  <div className="asx-strong">{detail.user?.name}</div>
                  <div className="asx-muted">{detail.user?.email}</div>
                  <div className="asec-detail-meta">
                    Score <strong>{detail.user?.security?.fraudScore ?? 0}</strong> ·{" "}
                    <span className={`asec-band ${bandClass(detail.user?.security?.fraudBand)}`}>
                      {detail.user?.security?.fraudBand || "CLEAN"}
                    </span>{" "}
                    · status {detail.user?.security?.status || "none"} ·{" "}
                    {detail.user?.validUserAt ? "counts as valid ✅" : "not valid"}
                  </div>
                  {detail.user?.referredBy ? (
                    <div className="asx-muted">Referred by: {detail.user.referredBy.name}</div>
                  ) : null}
                </div>

                <h4 className="asec-h4">Current signal breakdown (why this score)</h4>
                {Array.isArray(detail.user?.security?.breakdown) && detail.user.security.breakdown.length ? (
                  <ul className="asec-breakdown">
                    {detail.user.security.breakdown.map((b, i) => (
                      <li key={i}>
                        <span className="asec-bd-weight">+{b.weight}</span>
                        <span className="asec-bd-label">{b.label || b.rule}</span>
                        <span className="asec-bd-detail">{b.detail}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="asx-muted">No signals fired.</div>
                )}

                <h4 className="asec-h4">History (append-only fraud log)</h4>
                <div className="asec-history">
                  {(detail.history || []).map((h) => (
                    <div key={h._id} className="asec-hist-row">
                      <span className={`asec-band ${bandClass(h.band)}`}>{h.band}</span>
                      <span className="asec-hist-event">{h.event}</span>
                      <span className="asec-hist-score">{h.score}</span>
                      <span className="asec-hist-at">{fmt(h.createdAt)}</span>
                      {h.actionTaken ? <div className="asec-hist-action">{h.actionTaken}</div> : null}
                    </div>
                  ))}
                </div>

                <div className="asec-detail-actions">
                  {detail.user?.security?.status !== "confirmed_fraud" && (
                    <button
                      className="asx-btn asx-btn-danger asx-btn-sm"
                      onClick={() => confirmFraud(detail.user._id)}
                    >
                      Confirm fraud (permanent)
                    </button>
                  )}
                  {detail.user?.security?.status !== "false_positive" && (
                    <button
                      className="asx-btn asx-btn-ok asx-btn-sm"
                      onClick={() => falsePositive(detail.user._id)}
                    >
                      Mark false positive
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
